import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
  MAX_TILE_TEXT,
  correctAnswerText,
  type JeopardyClueBody,
  type JeopardyQuestionType,
  type JeopardySharedColumn,
} from '@gsf/games';
import { bank } from './content';

/**
 * Per-round panel: board size (own Apply, warns before shrinking past written clues) and
 * a bulk edit of every clue's config, where mixed fields sit behind an Override checkbox.
 */

const MIN_COLUMNS = 1;
const MAX_COLUMNS = 10;
const MIN_ROWS = 1;
const MAX_ROWS = 8;
const MAX_CHOICES_HINT = 8;

/** The stored scoring kinds plus "increasing", which generates ordinary `static`. */
type ScoringChoice = 'static' | 'increasing' | 'wager' | 'accrued';

const QUESTION_TYPES: ReadonlyArray<{ value: JeopardyQuestionType; label: string }> = [
  { value: 'verbal', label: 'Verbal (buzz in)' },
  { value: 'textInput', label: 'Text answer' },
  { value: 'numericInput', label: 'Number answer' },
  { value: 'multipleChoice', label: 'Multiple choice' },
  { value: 'multipleAnswer', label: 'Multiple answer' },
];

const ANSWER_TYPES = new Set<JeopardyQuestionType>(['verbal', 'textInput', 'numericInput']);

interface Props {
  round: number;
  /** The round's columns as the board shows them, for the current size and the shrink warning. */
  board: readonly JeopardySharedColumn[];
  /** Grow or shrink the round to `columns × rows`. */
  onResize: (columns: number, rows: number) => Promise<void>;
  onClose: () => void;
  notify: (message: string) => void;
}

function Stepper({
  label,
  value,
  min,
  max,
  onChange,
  disabled,
}: {
  label: string;
  value: number;
  min: number;
  max: number;
  onChange: (next: number) => void;
  disabled?: boolean;
}) {
  const clamp = (n: number) => Math.max(min, Math.min(max, n));
  return (
    <div className="field size-stepper">
      <label>{label}</label>
      <div className="row" style={{ alignItems: 'center', gap: '0.4rem' }}>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled || value <= min}
          aria-label={`Fewer ${label.toLowerCase()}`}
          onClick={() => onChange(clamp(value - 1))}
        >
          −
        </button>
        <strong style={{ minWidth: '1.5rem', textAlign: 'center' }}>{value}</strong>
        <button
          type="button"
          className="btn btn-sm"
          disabled={disabled || value >= max}
          aria-label={`More ${label.toLowerCase()}`}
          onClick={() => onChange(clamp(value + 1))}
        >
          +
        </button>
      </div>
    </div>
  );
}

/** One field's summary across the round: is it mixed, and if uniform, what value. */
function summarize<T>(values: readonly T[], key: (v: T) => string): { mixed: boolean } {
  const seen = new Set(values.map(key));
  return { mixed: seen.size > 1 };
}

export function RoundEditor({ round, board, onResize, onClose, notify }: Props) {
  const [clues, setClues] = useState<Array<{ id: string; tierIndex: number; body: JeopardyClueBody }>>([]);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  // Current size, from the board. Columns are categories; rows the tallest column.
  const currentCols = board.length;
  const currentRows = board.reduce((max, col) => Math.max(max, col.cells.length), 0);
  const [cols, setCols] = useState(Math.max(MIN_COLUMNS, currentCols));
  const [rows, setRows] = useState(Math.max(MIN_ROWS, currentRows));

  const [qtOverride, setQtOverride] = useState(false);
  const [qt, setQt] = useState<JeopardyQuestionType>('verbal');
  const [scOverride, setScOverride] = useState(false);
  const [sc, setSc] = useState<ScoringChoice>('static');
  const [points, setPoints] = useState(100);
  const [base, setBase] = useState(100);
  const [step, setStep] = useState(100);
  const [maxWager, setMaxWager] = useState(1000);
  const [pool, setPool] = useState<number[]>([]);
  const [poolDraft, setPoolDraft] = useState('');
  const [negOverride, setNegOverride] = useState(false);
  const [negOn, setNegOn] = useState(false);
  const [negAmt, setNegAmt] = useState(100);
  const [tileOverride, setTileOverride] = useState(false);
  const [tileKind, setTileKind] = useState<'points' | 'custom'>('points');
  const [tileText, setTileText] = useState('');

  const [pendingApply, setPendingApply] = useState<string | null>(null);
  const [pendingResize, setPendingResize] = useState<string | null>(null);

  // Seeded values: a uniform field applies only when moved off its seed, so an untouched
  // Apply never rewrites the round to the form's defaults.
  const seedRef = useRef({
    qt: 'verbal' as JeopardyQuestionType,
    sc: 'static' as ScoringChoice,
    points: 100,
    maxWager: 1000,
    negOn: false,
    negAmt: 100,
    tileKind: 'points' as 'points' | 'custom',
    tileText: '',
    pool: [] as number[],
  });

  /** Turn every Override back off and discard staged values. */
  const resetOverrides = useCallback(() => {
    setQtOverride(false);
    setScOverride(false);
    setNegOverride(false);
    setTileOverride(false);
    setPendingApply(null);
  }, []);

  const initStagedForm = useCallback((bodies: JeopardyClueBody[], poolValues: number[]) => {
    const seed = { ...seedRef.current, pool: poolValues };
    if (bodies.length > 0) {
      const first = bodies[0]!;
      const allSame = (key: (b: JeopardyClueBody) => string) =>
        bodies.every((b) => key(b) === key(first));

      if (allSame((b) => b.questionType)) seed.qt = first.questionType;

      if (allSame((b) => b.scoring.kind)) {
        if (first.scoring.kind === 'static') {
          seed.sc = 'static';
          if (allSame((b) => String((b.scoring as { points: number }).points))) {
            seed.points = (first.scoring as { points: number }).points;
          }
        } else if (first.scoring.kind === 'accrued') {
          seed.sc = 'accrued';
          if (allSame((b) => String((b.scoring as { maxWager: number }).maxWager))) {
            seed.maxWager = (first.scoring as { maxWager: number }).maxWager;
          }
        } else {
          seed.sc = 'wager';
        }
      }

      if (allSame((b) => (b.negativePoints === undefined ? 'off' : String(b.negativePoints)))) {
        seed.negOn = first.negativePoints !== undefined;
        if (first.negativePoints !== undefined) seed.negAmt = first.negativePoints;
      }

      if (allSame((b) => JSON.stringify(b.tileDisplay))) {
        seed.tileKind = first.tileDisplay.kind;
        if (first.tileDisplay.kind === 'custom') seed.tileText = first.tileDisplay.text;
      }
    }

    seedRef.current = seed;
    setQt(seed.qt);
    setSc(seed.sc);
    setPoints(seed.points);
    setMaxWager(seed.maxWager);
    setNegOn(seed.negOn);
    setNegAmt(seed.negAmt);
    setTileKind(seed.tileKind);
    setTileText(seed.tileText);
    setPool(poolValues);
  }, []);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [bodies, poolValues] = await Promise.all([
        bank.roundClueBodies(round),
        bank.getWagerPool(round),
      ]);
      setClues(bodies);
      // Seed from the round's uniform value, or an untouched Apply converts e.g. a
      // `textInput` round to the `verbal` default.
      initStagedForm(bodies.map((b) => b.body), poolValues);
      resetOverrides();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not load the round.');
    } finally {
      setLoading(false);
    }
  }, [round, notify, resetOverrides, initStagedForm]);

  useEffect(() => {
    void load();
  }, [load]);

  // Escape closes the popup, matching the clue editor.
  useEffect(() => {
    function onKey(event: KeyboardEvent) {
      if (event.key === 'Escape') onClose();
    }
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [onClose]);

  // Keep the size steppers tracking the board as it changes underneath (a resize elsewhere,
  // or this editor's own apply landing back through the snapshot).
  useEffect(() => {
    setCols(Math.max(MIN_COLUMNS, currentCols));
    setRows(Math.max(MIN_ROWS, currentRows));
  }, [currentCols, currentRows]);

  const summary = useMemo(() => {
    const bodies = clues.map((c) => c.body);
    const staticPts = bodies.filter((b) => b.scoring.kind === 'static');
    return {
      questionType: summarize(bodies, (b) => b.questionType),
      scoringKind: summarize(bodies, (b) => b.scoring.kind),
      staticPoints: summarize(staticPts, (b) => String((b.scoring as { points: number }).points)),
      negative: summarize(bodies, (b) => (b.negativePoints === undefined ? 'off' : String(b.negativePoints))),
      tile: summarize(bodies, (b) => JSON.stringify(b.tileDisplay)),
      uniformScoringKind: bodies.length > 0 && !summarize(bodies, (b) => b.scoring.kind).mixed
        ? bodies[0]!.scoring.kind
        : null,
    };
  }, [clues]);

  const N = cols * rows;
  const empty = clues.length === 0;

  // Negative points is meaningless on wager scoring.
  const effectiveWager = scOverride ? sc === 'wager' : summary.uniformScoringKind === 'wager';
  const negBlocked = effectiveWager;

  // Uniform fields are written only when dirty, mixed ones only with Override on, so an
  // untouched Apply changes nothing.
  const seed = seedRef.current;
  const poolChanged = JSON.stringify(pool) !== JSON.stringify(seed.pool);
  const dirty = {
    questionType: qt !== seed.qt,
    scoring:
      sc !== seed.sc ||
      (sc === 'static' && points !== seed.points) ||
      sc === 'increasing' ||
      (sc === 'accrued' && maxWager !== seed.maxWager) ||
      (sc === 'wager' && poolChanged),
    negative: negOn !== seed.negOn || (negOn && negAmt !== seed.negAmt),
    tile: tileKind !== seed.tileKind || (tileKind === 'custom' && tileText !== seed.tileText),
  };
  const willApply = {
    questionType: summary.questionType.mixed ? qtOverride : dirty.questionType,
    scoring: summary.scoringKind.mixed ? scOverride : dirty.scoring,
    negative: (summary.negative.mixed ? negOverride : dirty.negative) && !negBlocked,
    tile: summary.tile.mixed ? tileOverride : dirty.tile,
  };

  function retype(body: JeopardyClueBody, next: JeopardyQuestionType): JeopardyClueBody | null {
    const common = {
      categoryId: body.categoryId,
      prompt: body.prompt,
      position: body.position,
      scoring: body.scoring,
      tileDisplay: body.tileDisplay,
      lockout: body.lockout,
      answerSeconds: body.answerSeconds,
      ...(body.negativePoints !== undefined ? { negativePoints: body.negativePoints } : {}),
    };
    if (ANSWER_TYPES.has(next)) {
      // The three answer types share `correctAnswer`; preserve the clue's answer text.
      const correctAnswer =
        'correctAnswer' in body ? body.correctAnswer : correctAnswerText(body);
      return { ...common, questionType: next, correctAnswer } as JeopardyClueBody;
    }
    // A choice type needs choices; a clue with none is left as it was rather than
    // inventing answers.
    const choices = 'choices' in body ? body.choices : [];
    const correctChoiceIds = 'correctChoiceIds' in body ? body.correctChoiceIds : [];
    if (next === 'multipleChoice' && correctChoiceIds.length !== 1) return null;
    if (next === 'multipleAnswer' && correctChoiceIds.length < 1) return null;
    if (next === 'multipleChoice') {
      return { ...common, questionType: 'multipleChoice', choices, correctChoiceIds } as JeopardyClueBody;
    }
    return {
      ...common,
      questionType: 'multipleAnswer',
      choices,
      correctChoiceIds,
      ...('partialScoring' in body ? { partialScoring: body.partialScoring } : {}),
    } as JeopardyClueBody;
  }

  /** The clue's new body, or `{ skipped: true }` when a type conversion cannot be valid. */
  function nextBody(
    body: JeopardyClueBody,
    tierIndex: number,
  ): { body: JeopardyClueBody } | { skipped: true } {
    let out: JeopardyClueBody = body;
    if (willApply.questionType && qt !== body.questionType) {
      const retyped = retype(body, qt);
      if (!retyped) return { skipped: true };
      out = retyped;
    }

    if (willApply.scoring) {
      const scoring =
        sc === 'static'
          ? { kind: 'static' as const, points }
          : sc === 'increasing'
            ? { kind: 'static' as const, points: base + step * tierIndex }
            : sc === 'accrued'
              ? { kind: 'accrued' as const, maxWager }
              : { kind: 'wager' as const };
      out = { ...out, scoring } as JeopardyClueBody;
    }

    // negativePoints is a modifier; the schema rejects it on wager scoring, so strip it
    // there whether or not the field itself is being applied.
    if (out.scoring.kind === 'wager') {
      out = stripNegative(out);
    } else if (willApply.negative) {
      out = negOn ? { ...out, negativePoints: negAmt } : stripNegative(out);
    }

    if (willApply.tile) {
      out = {
        ...out,
        tileDisplay: tileKind === 'points' ? { kind: 'points' } : { kind: 'custom', text: tileText },
      } as JeopardyClueBody;
    }
    return { body: out };
  }

  function stripNegative(body: JeopardyClueBody): JeopardyClueBody {
    const { negativePoints: _drop, ...rest } = body as JeopardyClueBody & { negativePoints?: number };
    return rest as JeopardyClueBody;
  }

  function describeChanges(): string[] {
    const parts: string[] = [];
    if (willApply.questionType) parts.push('Question type');
    if (willApply.scoring) parts.push('Scoring');
    if (willApply.negative) parts.push('Negative points');
    if (willApply.tile) parts.push('Tile display');
    return parts;
  }

  function requestApply() {
    if (empty) {
      notify('There are no questions in this round yet.');
      return;
    }
    // Nothing changed: guidance rather than a silent no-op.
    const changes = describeChanges();
    if (changes.length === 0) {
      notify('Turn on Override and pick a value for at least one setting.');
      return;
    }
    setPendingApply(
      `This will set ${changes.join(', ')} for all ${clues.length} question${
        clues.length === 1 ? '' : 's'
      } in this round, overwriting whatever each one currently has.`,
    );
  }

  async function confirmApply() {
    setPendingApply(null);
    setBusy(true);
    let skipped = 0;
    try {
      for (const clue of clues) {
        const result = nextBody(clue.body, clue.tierIndex);
        if ('skipped' in result) {
          skipped += 1;
          continue;
        }
        if (JSON.stringify(result.body) !== JSON.stringify(clue.body)) {
          await bank.saveClueBody(clue.id, result.body);
        }
      }
      // The pool is round content; author it whenever wager scoring is what was applied.
      if (willApply.scoring && sc === 'wager') await bank.saveWagerPool(round, pool);
      notify(
        skipped > 0
          ? `Applied. ${skipped} question${skipped === 1 ? '' : 's'} could not switch to a choice type (no correct answer marked) and were left as they were.`
          : 'Applied to the round.',
      );
      await load();
    } catch (err) {
      notify(err instanceof Error ? err.message : 'Could not apply the changes.');
    } finally {
      setBusy(false);
    }
  }

  function requestResize() {
    if (cols === currentCols && rows === currentRows) return;
    // Which tiles a shrink would remove, and how many carry written clues.
    let removedWritten = 0;
    board.forEach((col, ci) => {
      col.cells.forEach((cell, ri) => {
        if ((ci >= cols || ri >= rows) && !cell.draft) removedWritten += 1;
      });
    });
    if (cols < currentCols || rows < currentRows) {
      setPendingResize(
        removedWritten > 0
          ? `Shrinking to ${cols} × ${rows} removes ${removedWritten} written question${
              removedWritten === 1 ? '' : 's'
            }. This cannot be undone.`
          : `Shrink this round to ${cols} × ${rows}?`,
      );
      return;
    }
    void applyResize();
  }

  async function applyResize() {
    setPendingResize(null);
    setBusy(true);
    try {
      await onResize(cols, rows);
    } finally {
      setBusy(false);
    }
  }

  // Live tier preview for the increasing-value generator.
  const increasingPreview = Array.from({ length: Math.max(1, rows) }, (_, i) => base + step * i).join(' / ');

  function addPoolValue() {
    const n = Number(poolDraft.trim());
    if (!Number.isInteger(n) || n <= 0) {
      notify('A wager value is a whole number of points.');
      return;
    }
    setPool((p) => [...p, n]);
    setPoolDraft('');
  }

  const applyDisabled = busy || loading || empty;

  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div
        className="modal modal-wide round-editor"
        role="dialog"
        aria-label="Round editor"
        onClick={(event) => event.stopPropagation()}
      >
      <div className="row" style={{ alignItems: 'center' }}>
        <h2 className="panel-title" style={{ margin: 0 }}>
          Round editor
        </h2>
        <span className="spacer" />
        <button type="button" className="btn btn-sm" onClick={onClose}>
          Close
        </button>
      </div>

      {loading ? <p className="faint">Loading the round…</p> : null}

      <section className="round-editor-section">
        <h3 className="round-editor-heading">Board size</h3>
        <div className="row" style={{ alignItems: 'flex-end', gap: '1rem', flexWrap: 'wrap' }}>
          <Stepper label="Categories" value={cols} min={MIN_COLUMNS} max={MAX_COLUMNS} onChange={setCols} disabled={busy} />
          <Stepper label="Tiers" value={rows} min={MIN_ROWS} max={MAX_ROWS} onChange={setRows} disabled={busy} />
          <button
            type="button"
            className="btn btn-secondary"
            disabled={busy || (cols === currentCols && rows === currentRows)}
            onClick={requestResize}
          >
            Resize to {cols} × {rows}
          </button>
        </div>
        {pendingResize ? (
          <div className="round-editor-warn">
            <p style={{ margin: 0 }}>{pendingResize}</p>
            <div className="row">
              <button type="button" className="btn btn-danger btn-sm" disabled={busy} onClick={() => void applyResize()}>
                Resize anyway
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setPendingResize(null)}>
                Keep it
              </button>
            </div>
          </div>
        ) : null}
      </section>

      <section className="round-editor-section">
        <h3 className="round-editor-heading">Every question in this round</h3>
        {empty && !loading ? (
          <p className="faint">This round has no questions yet — set a board size above first.</p>
        ) : null}

        <MixedField
          label="Question type"
          mixed={summary.questionType.mixed}
          override={qtOverride}
          onOverride={setQtOverride}
        >
          <select
            aria-label="Bulk question type"
            disabled={summary.questionType.mixed && !qtOverride}
            value={qt}
            onChange={(e) => setQt(e.target.value as JeopardyQuestionType)}
          >
            {QUESTION_TYPES.map((t) => (
              <option key={t.value} value={t.value}>
                {t.label}
              </option>
            ))}
          </select>
          {!ANSWER_TYPES.has(qt) ? (
            <span className="faint">
              Choice types keep each question's existing choices; a question with none marked is left alone.
            </span>
          ) : null}
        </MixedField>

        <MixedField label="Scoring" mixed={summary.scoringKind.mixed} override={scOverride} onOverride={setScOverride}>
          <select
            aria-label="Bulk scoring"
            disabled={summary.scoringKind.mixed && !scOverride}
            value={sc}
            onChange={(e) => setSc(e.target.value as ScoringChoice)}
          >
            <option value="static">Fixed point value</option>
            <option value="increasing">Increasing point value</option>
            <option value="accrued">Accrued wager (own score)</option>
            <option value="wager">Round wager pool</option>
          </select>
          {sc === 'static' ? (
            <label className="field">
              <span>Points{summary.staticPoints.mixed ? ' (mixed)' : ''}</span>
              <input
                type="number"
                min={1}
                value={points}
                disabled={summary.scoringKind.mixed && !scOverride}
                onChange={(e) => setPoints(Math.max(1, Number(e.target.value) || 0))}
              />
            </label>
          ) : null}
          {sc === 'increasing' ? (
            <div className="col" style={{ gap: '0.4rem' }}>
              <div className="row" style={{ gap: '0.75rem' }}>
                <label className="field">
                  <span>Tier 1</span>
                  <input type="number" min={0} value={base} onChange={(e) => setBase(Math.max(0, Number(e.target.value) || 0))} />
                </label>
                <label className="field">
                  <span>Per tier</span>
                  <input type="number" min={0} value={step} onChange={(e) => setStep(Math.max(0, Number(e.target.value) || 0))} />
                </label>
              </div>
              <span className="faint">Tiers: {increasingPreview}</span>
            </div>
          ) : null}
          {sc === 'accrued' ? (
            <label className="field">
              <span>Max wager</span>
              <input type="number" min={1} value={maxWager} onChange={(e) => setMaxWager(Math.max(1, Number(e.target.value) || 0))} />
            </label>
          ) : null}
          {sc === 'wager' ? (
            <div className="col round-editor-pool" style={{ gap: '0.4rem' }}>
              <span className="faint">
                The pool this round draws from — each value used once. {pool.length} value{pool.length === 1 ? '' : 's'} for{' '}
                {N} tile{N === 1 ? '' : 's'}.
              </span>
              <div className="row" style={{ flexWrap: 'wrap', gap: '0.4rem' }}>
                {pool.map((value, i) => (
                  <span key={`${value}-${i}`} className="chip">
                    {value}
                    <button
                      type="button"
                      className="chip-x"
                      aria-label={`Remove ${value}`}
                      onClick={() => setPool((p) => p.filter((_, j) => j !== i))}
                    >
                      ×
                    </button>
                  </span>
                ))}
              </div>
              <div className="row" style={{ gap: '0.4rem' }}>
                <input
                  type="number"
                  min={1}
                  aria-label="Add a wager value"
                  placeholder="Add a value"
                  value={poolDraft}
                  onChange={(e) => setPoolDraft(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addPoolValue();
                    }
                  }}
                />
                <button type="button" className="btn btn-sm" onClick={addPoolValue}>
                  Add
                </button>
              </div>
              {pool.length > 0 && pool.length < N ? (
                <span className="board-todo">Fewer values than tiles — a wager clue past the pool cannot be played.</span>
              ) : null}
            </div>
          ) : null}
        </MixedField>

        <MixedField
          label="Negative points"
          mixed={summary.negative.mixed}
          override={negOverride}
          onOverride={setNegOverride}
          disabled={negBlocked}
          disabledNote={negBlocked ? 'Not available with wager scoring.' : undefined}
        >
          <label className="field-check">
            <input
              type="checkbox"
              disabled={negBlocked || (summary.negative.mixed && !negOverride)}
              checked={negOn}
              onChange={(e) => setNegOn(e.target.checked)}
            />
            Deduct on a wrong answer
          </label>
          {negOn ? (
            <label className="field">
              <span>Amount</span>
              <input
                type="number"
                min={0}
                value={negAmt}
                disabled={negBlocked || (summary.negative.mixed && !negOverride)}
                onChange={(e) => setNegAmt(Math.max(0, Number(e.target.value) || 0))}
              />
            </label>
          ) : null}
        </MixedField>

        <MixedField label="Tile display" mixed={summary.tile.mixed} override={tileOverride} onOverride={setTileOverride}>
          <label className="field-check">
            <input
              type="radio"
              name="round-tile"
              disabled={summary.tile.mixed && !tileOverride}
              checked={tileKind === 'points'}
              onChange={() => setTileKind('points')}
            />
            Show the points
          </label>
          <label className="field-check">
            <input
              type="radio"
              name="round-tile"
              disabled={summary.tile.mixed && !tileOverride}
              checked={tileKind === 'custom'}
              onChange={() => setTileKind('custom')}
            />
            Show custom text
          </label>
          {tileKind === 'custom' ? (
            <input
              type="text"
              aria-label="Tile text"
              placeholder="Tile text"
              value={tileText}
              maxLength={MAX_TILE_TEXT}
              disabled={summary.tile.mixed && !tileOverride}
              onChange={(e) => setTileText(e.target.value)}
            />
          ) : null}
        </MixedField>

        {pendingApply ? (
          <div className="round-editor-warn">
            <p style={{ margin: 0 }}>{pendingApply}</p>
            <div className="row">
              <button type="button" className="btn btn-primary btn-sm" disabled={busy} onClick={() => void confirmApply()}>
                Apply to all {clues.length}
              </button>
              <button type="button" className="btn btn-sm" onClick={() => setPendingApply(null)}>
                Keep editing
              </button>
            </div>
          </div>
        ) : (
          <div className="row" style={{ marginTop: '0.5rem' }}>
            <button type="button" className="btn btn-primary" disabled={applyDisabled} onClick={requestApply}>
              Apply to all {clues.length}
            </button>
            <button type="button" className="btn" disabled={busy} onClick={resetOverrides}>
              Cancel
            </button>
          </div>
        )}
        <p className="faint" style={{ margin: 0 }}>
          A choice type can hold up to {MAX_CHOICES_HINT} options — set each question's choices in its own editor (✎).
        </p>
      </section>
      </div>
    </div>
  );
}

/**
 * One bulk field. The Override checkbox only appears when the round disagrees; the
 * control is disabled until it is on.
 */
function MixedField({
  label,
  mixed,
  override,
  onOverride,
  disabled,
  disabledNote,
  children,
}: {
  label: string;
  mixed: boolean;
  override: boolean;
  onOverride: (on: boolean) => void;
  disabled?: boolean;
  disabledNote?: string;
  children: React.ReactNode;
}) {
  return (
    <div className={`round-field${mixed ? ' is-mixed' : ''}`}>
      <div className="row" style={{ alignItems: 'center', gap: '0.5rem' }}>
        <strong>{label}</strong>
        {mixed && !disabled ? (
          <label className="field-check" style={{ margin: 0 }}>
            <input type="checkbox" checked={override} onChange={(e) => onOverride(e.target.checked)} />
            Override
          </label>
        ) : null}
        {mixed && !disabled && !override ? <span className="faint">Mixed across round</span> : null}
        {disabled && disabledNote ? <span className="faint">{disabledNote}</span> : null}
      </div>
      {!disabled ? <div className="col round-field-body">{children}</div> : null}
    </div>
  );
}
